import { apiReferenceConfigurationWithSourceSchema } from '@scalar/schemas/api-reference'
import type {
  AnyApiReferenceConfiguration,
  ApiReferenceConfigurationWithSource,
  CreateApiReference,
} from '@scalar/types/api-reference'
import { createHead } from '@unhead/vue/client'
import { createApp, createSSRApp, h, reactive } from 'vue'

import { default as ApiReference } from '@/components/ApiReference.vue'
import { hasPluginUrls, loadPluginsFromUrls } from '@/standalone/lib/load-plugins-from-urls'

const getSpecScriptTag = (doc: Document) => doc.getElementById('api-reference')

/**
 * The id given to the standalone build's single injected `<style>` tag.
 * Keep in sync with `vite.standalone.config.ts` and `vite.standalone.esm.config.ts`.
 */
const STANDALONE_STYLE_ID = 'scalar-style'

/**
 * Per-document bookkeeping for the standalone build's injected styles.
 *
 * The CDN build injects all of its CSS into one `<style>` tag in `<head>`. Under
 * SPA-style navigation (Turbo Drive, htmx boost, Astro view transitions) the host
 * swaps the DOM without reloading the window, so those document-level styles
 * (`@layer scalar-base`, the `:root` theme variables) would otherwise linger and
 * bleed into the host app's next page. We reference-count the live instances and
 * detach the styles when the last one is destroyed, re-attaching them when a new
 * instance mounts so navigating back to the reference is still styled.
 *
 * State is keyed by document so the counter survives navigations (the JS context
 * persists) while staying isolated per page.
 */
const standaloneStyleState = new WeakMap<Document, { count: number; detachedStyle: HTMLStyleElement | null }>()

const getStandaloneStyleState = (doc: Document): { count: number; detachedStyle: HTMLStyleElement | null } => {
  const existing = standaloneStyleState.get(doc)
  if (existing) {
    return existing
  }

  const state = { count: 0, detachedStyle: null }
  standaloneStyleState.set(doc, state)

  return state
}

/** Track a freshly mounted instance and restore previously detached styles. */
const retainStandaloneStyles = (doc: Document): void => {
  const state = getStandaloneStyleState(doc)
  state.count += 1

  if (state.detachedStyle && !doc.getElementById(STANDALONE_STYLE_ID)) {
    doc.head.appendChild(state.detachedStyle)
    state.detachedStyle = null
  }
}

/** Release an instance and detach the injected styles once the last one is gone. */
const releaseStandaloneStyles = (doc: Document): void => {
  const state = getStandaloneStyleState(doc)
  state.count = Math.max(0, state.count - 1)

  if (state.count > 0) {
    return
  }

  const styleElement = doc.getElementById(STANDALONE_STYLE_ID)
  if (styleElement instanceof HTMLStyleElement) {
    state.detachedStyle = styleElement
    styleElement.remove()
  }
}

/**
 * Reading the configuration from the data-attributes.
 */
export function getConfigurationFromDataAttributes(doc: Document): ApiReferenceConfigurationWithSource {
  const specElement = doc.querySelector('[data-spec]')
  const specUrlElement = doc.querySelector('[data-spec-url]')
  const configurationScriptElement = doc.querySelector('#api-reference[data-configuration]')

  const getConfiguration = () => {
    // <script data-configuration="{ … }" />
    if (configurationScriptElement) {
      const configurationFromElement = configurationScriptElement.getAttribute('data-configuration')

      if (configurationFromElement) {
        return {
          _integration: 'html',
          ...JSON.parse(configurationFromElement.split('&quot;').join('"')),
        }
      }
    }

    return apiReferenceConfigurationWithSourceSchema({ _integration: 'html' })
  }

  const getUrl = () => {
    // Let's first check if the user passed a spec URL in the configuration.
    if (getConfiguration().url) {
      return getConfiguration().url
    }

    if (getConfiguration().spec?.url) {
      return getConfiguration().spec?.url
    }

    // <script id="api-reference" data-url="/scalar.json" />
    const specScriptTag = getSpecScriptTag(doc)
    if (specScriptTag) {
      const urlFromScriptTag = specScriptTag.getAttribute('data-url')?.trim()

      if (urlFromScriptTag) {
        return urlFromScriptTag
      }
    }

    // <div data-spec-url="/scalar.json" />
    if (specUrlElement) {
      console.warn(
        '[@scalar/api-reference] The [data-spec-url] HTML API is deprecated. Use the new <script id="api-reference" data-url="/scalar.json" /> API instead.',
      )
      const urlFromSpecUrlElement = specUrlElement.getAttribute('data-spec-url')

      if (urlFromSpecUrlElement) {
        return urlFromSpecUrlElement
      }
    }

    return undefined
  }

  const getContent = (): string | undefined => {
    // <script id="api-reference" type="application/json">{"openapi":"3.1.0","info":{"title":"Example"},"paths":{}}</script>
    const specScriptTag = getSpecScriptTag(doc)
    if (specScriptTag) {
      const specFromScriptTag = specScriptTag.innerHTML?.trim()

      if (specFromScriptTag) {
        return specFromScriptTag
      }
    }

    // <div data-spec='{"openapi":"3.1.0","info":{"title":"Example"},"paths":{}}' />
    if (specElement) {
      console.warn(
        '[@scalar/api-reference] The [data-spec] HTML API is deprecated. Use the new <script id="api-reference" type="application/json">{"openapi":"3.1.0","info":{"title":"Example"},"paths":{}}</script> API instead.',
      )
      const specFromSpecElement = specElement.getAttribute('data-spec')?.trim()

      if (specFromSpecElement) {
        return specFromSpecElement
      }
    }

    return undefined
  }

  const getProxyUrl = () => {
    // <script id="api-reference" data-proxy-url="https://proxy.scalar.com">…</script>
    const specScriptTag = getSpecScriptTag(doc)
    if (specScriptTag) {
      const proxyUrl = specScriptTag.getAttribute('data-proxy-url')

      if (proxyUrl) {
        return proxyUrl.trim()
      }
    }

    return undefined
  }

  // Ensure Reference Props are reactive
  if (!specUrlElement && !specElement && !getSpecScriptTag(doc)) {
    // Stay quiet.
  } else {
    const urlOrContent = getContent() ? { content: getContent() } : { url: getUrl() }

    return apiReferenceConfigurationWithSourceSchema({
      _integration: 'html',
      proxyUrl: getProxyUrl(),
      ...getConfiguration(),
      ...urlOrContent,
    })
  }

  return apiReferenceConfigurationWithSourceSchema({ _integration: 'html' })
}

/**
 * Mount the Scalar API Reference on a given document.
 * Read the HTML data-attributes for configuration.
 */
export function findDataAttributes(doc: Document, configuration: ApiReferenceConfigurationWithSource) {
  /** @deprecated Use the new <script id="api-reference" data-url="/scalar.json" /> API instead. */
  const specElement = doc.querySelector('[data-spec]')
  /** @deprecated Use the new <script id="api-reference" data-url="/scalar.json" /> API instead. */
  const specUrlElement = doc.querySelector('[data-spec-url]')

  if (configuration?.darkMode) {
    doc.body?.classList.add('dark-mode')
  } else {
    doc.body?.classList.add('light-mode')
  }

  const container = createContainer(doc, specElement || specUrlElement)

  if (container) {
    createApiReference(container, configuration)
  }
}

// If it's a script tag, we can't mount the Vue.js app inside that tag.
// We need to add a new container element before the script tag.
export const createContainer = (doc: Document, element?: Element | null) => {
  let _container: Element | null = null

  const specScriptTag = getSpecScriptTag(doc)

  if (specScriptTag) {
    _container = doc.createElement('div')
    specScriptTag?.parentNode?.insertBefore(_container, specScriptTag)
  } else if (element) {
    _container = element
  }

  return _container
}

/**
 * Create (and mount) a new Scalar API Reference
 *
 * @example createApiReference({ url: '/scalar.json' }).mount('#app')
 * @example createApiReference('#app', { url: '/scalar.json' })
 * @example createApiReference(document.getElementById('app'), { url: '/scalar.json' })
 */
export const createApiReference: CreateApiReference = (
  elementOrSelectorOrConfig,
  optionalConfiguration?: AnyApiReferenceConfiguration,
) => {
  // Create an id prefix for useId so we don't have collisions with other Vue apps
  const idPrefix = 'scalar-refs'

  const props = reactive<{ configuration: AnyApiReferenceConfiguration }>({
    // Either the configuration will be the second argument or it MUST be the first (configuration only)
    configuration: optionalConfiguration ?? (elementOrSelectorOrConfig as AnyApiReferenceConfiguration) ?? {},
  })

  const createReferenceApp = (isSsr = false) => {
    const referenceApp = isSsr ? createSSRApp(() => h(ApiReference, props)) : createApp(() => h(ApiReference, props))

    // Meta tags, etc.
    referenceApp.use(createHead())
    referenceApp.config.idPrefix = idPrefix

    return referenceApp
  }

  // If we have an optional config, then we must mount the element immediately (not sure why type is not narrowing)
  const mountElement = optionalConfiguration
    ? typeof elementOrSelectorOrConfig === 'string'
      ? document.querySelector(elementOrSelectorOrConfig)
      : (elementOrSelectorOrConfig as Element)
    : null

  // Detect server-rendered content and use createSSRApp for hydration
  const shouldHydrate = !!optionalConfiguration && !!mountElement && mountElement.children.length > 0
  let app = createReferenceApp(shouldHydrate)

  // Track whether this instance mounted so `destroy` only releases the shared
  // standalone styles for instances that actually retained them.
  let hasMounted = false

  if (optionalConfiguration) {
    if (mountElement) {
      const mount = () => {
        app.mount(mountElement)
        hasMounted = true
        retainStandaloneStyles(document)
      }

      if (hasPluginUrls(props.configuration)) {
        // Plugins referenced by URL must be resolved before the app mounts — plugin registration
        // happens once at first render and is not reactive, so a plugin that arrives later would
        // never be picked up.
        loadPluginsFromUrls(props.configuration).then(() => {
          // Skip mounting when the instance was destroyed while the plugins were loading.
          if (!abortController.signal.aborted) {
            mount()
          }
        })
      } else {
        mount()
      }
    } else {
      console.error('Could not find a mount point for API References:', elementOrSelectorOrConfig)
    }
  }

  // Tie the deprecated `document` listeners to an AbortController so `destroy`
  // can remove them all at once. Without this, every `createApiReference()`
  // call leaks three permanent listeners — visible during Astro view
  // transitions where each navigation creates a new instance.
  const abortController = new AbortController()
  const listenerOptions: AddEventListenerOptions = {
    capture: false,
    signal: abortController.signal,
  }

  /**
   * Reload the API Reference
   * @deprecated
   */
  document.addEventListener(
    'scalar:reload-references',
    () => {
      console.warn(
        'scalar:reload-references event has been deprecated, please use the scalarInstance.app.mount method instead.',
      )
      if (!props.configuration) {
        return
      }

      // Snag the current element
      const currentElement =
        typeof elementOrSelectorOrConfig === 'string'
          ? document.querySelector(elementOrSelectorOrConfig)
          : (elementOrSelectorOrConfig as Element)

      if (!currentElement) {
        return
      }

      // Ensure we re-attach the element if it was unmounted
      if (currentElement && !document.body.contains(currentElement)) {
        document.body.appendChild(currentElement)
      }

      // Create a new Vue app instance
      app.unmount()
      app = createReferenceApp()
      app.mount(currentElement)
    },
    listenerOptions,
  )

  /** Destroy the current API Reference instance */
  const destroy = () => {
    abortController.abort()
    props.configuration = {}

    // Only unmount an app that actually mounted. With `pluginUrls`, mounting is deferred until the
    // plugin modules resolve, so `destroy` can run first — unmounting then would just warn.
    if (hasMounted) {
      hasMounted = false
      app.unmount()
      releaseStandaloneStyles(document)
    }
  }

  /**
   * Allow user to destroy the API Reference
   * @deprecated
   */
  document.addEventListener(
    'scalar:destroy-references',
    () => {
      console.warn('scalar:destroy-references event has been deprecated, please use scalarInstance.destroy instead.')
      destroy()
    },
    listenerOptions,
  )

  /**
   * Allow user to update configuration
   * @deprecated
   */
  document.addEventListener(
    'scalar:update-references-config',
    (ev) => {
      console.warn(
        'scalar:update-references-config event has been deprecated, please use scalarInstance.updateConfiguration instead.',
      )
      if ('detail' in ev) {
        Object.assign(props, ev.detail)
      }
    },
    listenerOptions,
  )

  const instance = {
    app,
    getConfiguration: () => props.configuration ?? {},
    updateConfiguration: (newConfig: AnyApiReferenceConfiguration) => {
      props.configuration = newConfig
    },
    destroy,
  }

  return instance
}
